import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { ReceivingDmsLink, ReceivingDmsLinkKind } from '../types';
import { wmsReceivingKeys } from './useWmsReceiving';

const dmsLinkKeys = {
  list: (receivingOrderId: string) => ['wms-receiving-dms-links', receivingOrderId] as const,
};

export function useReceivingDmsLinks(receivingOrderId: string | undefined) {
  return useQuery({
    queryKey: dmsLinkKeys.list(receivingOrderId ?? ''),
    enabled: !!receivingOrderId,
    queryFn: () =>
      api
        .get<{ dmsLinks: ReceivingDmsLink[] }>(`/v4/wms/receiving/${receivingOrderId}/dms-links`)
        .then((r) => r.dmsLinks),
  });
}

interface AddInput {
  receivingOrderId: string;
  dmsDocumentId: string;
  kind?: ReceivingDmsLinkKind;
  label?: string | null;
}

export function useAddReceivingDmsLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ receivingOrderId, dmsDocumentId, kind, label }: AddInput) =>
      api.post<{ dmsLink: ReceivingDmsLink }>(`/v4/wms/receiving/${receivingOrderId}/dms-links`, {
        dmsDocumentId,
        kind,
        label,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: dmsLinkKeys.list(vars.receivingOrderId) });
      qc.invalidateQueries({ queryKey: wmsReceivingKeys.detail(vars.receivingOrderId) });
      toast.success('Document linked');
    },
    onError: (e) => {
      toast.error('Failed to link document', { description: e instanceof Error ? e.message : '' });
    },
  });
}

interface PatchInput {
  receivingOrderId: string;
  linkId: string;
  patch: Partial<Pick<ReceivingDmsLink, 'kind' | 'label' | 'sortOrder'>>;
}

export function useUpdateReceivingDmsLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ receivingOrderId, linkId, patch }: PatchInput) =>
      api.patch<{ dmsLink: ReceivingDmsLink }>(
        `/v4/wms/receiving/${receivingOrderId}/dms-links/${linkId}`,
        patch,
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: dmsLinkKeys.list(vars.receivingOrderId) });
    },
  });
}

interface DeleteInput {
  receivingOrderId: string;
  linkId: string;
}

export function useDeleteReceivingDmsLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ receivingOrderId, linkId }: DeleteInput) =>
      api.delete(`/v4/wms/receiving/${receivingOrderId}/dms-links/${linkId}`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: dmsLinkKeys.list(vars.receivingOrderId) });
      qc.invalidateQueries({ queryKey: wmsReceivingKeys.detail(vars.receivingOrderId) });
      toast.success('Document unlinked');
    },
  });
}
